import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare, CheckSquare, Clock, Lock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { formatRelativeTime } from '@/lib/utils';

interface Project {
  id: string;
  name: string;
  isPrivate?: boolean;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
  } | null;
  clientOrganization?: {
    id: string;
    name: string;
  } | null;
  _count?: {
    messages?: number;
    taskLists?: number;
  };
}

interface ProjectsCardProps {
  projects: Project[];
  maxProjects?: number;
}

export function ProjectsCard({ projects, maxProjects = 5 }: ProjectsCardProps) {
  const displayedProjects = projects.slice(0, maxProjects);
  const hasMore = projects.length > maxProjects;

  // Generate a solid color based on the project name
  const getColor = (name: string) => {
    const colors = [
      'bg-primary',
      'bg-accent',
      'bg-success',
      'bg-warning',
      'bg-error',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <Card padding="none">
      <div className="flex items-center justify-between px-4 py-3 border-b border-grey-100 bg-grey-50/50">
        <span className="text-sm font-medium text-secondary">Your Projects</span>
        <span className="text-xs text-grey-400">({projects.length})</span>
      </div>

      {projects.length === 0 ? (
        <div className="px-4 py-8 text-center">
          <p className="text-sm text-grey-500">No projects yet</p>
        </div>
      ) : (
        <div className="divide-y divide-grey-100">
          {displayedProjects.map((project) => (
            <Link
              key={project.id}
              to={`/projects/${project.id}`}
              className="flex items-center gap-3 p-4 hover:bg-grey-50 transition-colors group"
            >
              <div className={`w-10 h-10 rounded-lg ${getColor(project.name)} flex items-center justify-center text-white font-semibold shrink-0`}>
                {project.name[0]?.toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-medium text-secondary group-hover:text-accent transition-colors truncate flex items-center gap-1.5">
                    {project.isPrivate && (
                      <Lock className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
                    )}
                    {project.name}
                  </h3>
                  {(project.clientOrganization || project.organization) && (
                    <span className="badge-accent min-w-0 max-w-[45vw] truncate">{(project.clientOrganization ?? project.organization)!.name}</span>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs text-grey-500 mt-0.5">
                  <span className="flex items-center gap-1">
                    <MessageSquare className="w-3 h-3" />
                    {project._count?.messages || 0}
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckSquare className="w-3 h-3" />
                    {project._count?.taskLists || 0} lists
                  </span>
                  <span className="flex items-center gap-1 text-grey-400">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(project.updatedAt)}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {hasMore && (
        <div className="px-4 py-3 border-t border-grey-100">
          <Link
            to="/projects"
            className="text-sm font-medium text-accent hover:text-accent-700 flex items-center gap-1"
          >
            View all {projects.length} projects <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      )}
    </Card>
  );
}
